import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft, Save, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export function CadastrarEntradaPage() {
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState({
    tipo: '',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    responsavelTipo: '',
    responsavelNome: '',
    metodoPagamento: '',
    observacoes: ''
  })

  const [isLoading, setIsLoading] = useState(false)

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.tipo || !formData.valor || !formData.responsavelTipo) {
      alert('Por favor, preencha todos os campos obrigatórios.')
      return
    }

    setIsLoading(true)

    try {
      const novaEntrada = {
        tipo: formData.tipo as any,
        valor: parseFloat(formData.valor),
        data: formData.data,
        responsavel: {
          tipo: formData.responsavelTipo as any,
          nome: formData.responsavelNome || undefined
        },
        metodoPagamento: formData.metodoPagamento as any || undefined,
        observacoes: formData.observacoes || undefined,
        statusConciliacao: 'pendente' as const
      }

      // Aqui você salvaria no store
      console.log('Nova entrada:', novaEntrada)
      
      // Redirecionar para a listagem
      navigate('/financas/administracao/entradas')
    } catch (error) {
      console.error('Erro ao cadastrar entrada:', error)
      alert('Erro ao cadastrar entrada. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/financas/administracao')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Cadastrar Entrada Financeira</h1>
          <p className="text-muted-foreground">
            Registre uma nova entrada de dízimo, oferta ou doação
          </p>
        </div>
      </div>

      {/* Formulário */}
      <Card>
        <CardHeader>
          <CardTitle>Informações da Entrada</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo de Entrada *</Label>
                <Select 
                  value={formData.tipo} 
                  onValueChange={(value) => handleInputChange('tipo', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dizimo">Dízimo</SelectItem>
                    <SelectItem value="ofertas">Ofertas</SelectItem>
                    <SelectItem value="missoes">Missões</SelectItem>
                    <SelectItem value="doacoes">Doações</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="valor">Valor *</Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={formData.valor}
                  onChange={(e) => handleInputChange('valor', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="data">Data da Entrada *</Label>
                <Input
                  id="data"
                  type="date"
                  value={formData.data}
                  onChange={(e) => handleInputChange('data', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="responsavelTipo">Tipo de Responsável *</Label>
                <Select 
                  value={formData.responsavelTipo} 
                  onValueChange={(value) => handleInputChange('responsavelTipo', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="membro">Membro</SelectItem>
                    <SelectItem value="visitante">Visitante</SelectItem>
                    <SelectItem value="anonimo">Anônimo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.responsavelTipo !== 'anonimo' && (
                <div className="space-y-2">
                  <Label htmlFor="responsavelNome">Nome do Responsável</Label>
                  <Input
                    id="responsavelNome"
                    placeholder="Nome completo"
                    value={formData.responsavelNome}
                    onChange={(e) => handleInputChange('responsavelNome', e.target.value)}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="metodoPagamento">Método de Pagamento</Label>
                <Select 
                  value={formData.metodoPagamento} 
                  onValueChange={(value) => handleInputChange('metodoPagamento', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o método" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="transferencia">Transferência</SelectItem>
                    <SelectItem value="cartao">Cartão</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                placeholder="Observações adicionais (opcional)"
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                rows={3}
              />
            </div>

            {/* Botões */}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isLoading ? 'Salvando...' : 'Salvar Entrada'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}